/** Namespace UUID для детерминированных id карточек CRM (RFC 4122 v5). */
export const CRM_BOARD_ITEM_NAMESPACE = 'a1b2c3d4-e5f6-4789-a012-3456789abcde';

function parseUuidBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '');
  if (hex.length !== 32) throw new Error('invalid namespace uuid');
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/** SHA-1 (browser + Node, без node:crypto — для Vite/mini-app). */
function sha1(message: Uint8Array): Uint8Array {
  const ml = message.length;
  const withOne = new Uint8Array(((ml + 9 + 63) >> 6) << 6);
  withOne.set(message);
  withOne[ml] = 0x80;
  const view = new DataView(withOne.buffer);
  view.setUint32(withOne.length - 4, ml * 8, false);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;

  const w = new Uint32Array(80);
  for (let i = 0; i < withOne.length; i += 64) {
    for (let t = 0; t < 16; t++) {
      w[t] = view.getUint32(i + t * 4, false);
    }
    for (let t = 16; t < 80; t++) {
      w[t] = rotl(w[t - 3]! ^ w[t - 8]! ^ w[t - 14]! ^ w[t - 16]!, 1);
    }
    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let t = 0; t < 80; t++) {
      let f: number;
      let k: number;
      if (t < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (t < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (t < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp = (rotl(a, 5) + f + e + k + w[t]!) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30) >>> 0;
      b = a;
      a = temp;
    }
    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const out = new Uint8Array(20);
  const outView = new DataView(out.buffer);
  outView.setUint32(0, h0, false);
  outView.setUint32(4, h1, false);
  outView.setUint32(8, h2, false);
  outView.setUint32(12, h3, false);
  outView.setUint32(16, h4, false);
  return out;
}

function rotl(x: number, n: number): number {
  return ((x << n) | (x >>> (32 - n))) >>> 0;
}

function formatUuidV5(hash: Uint8Array): string {
  const b = hash.slice(0, 16);
  b[6] = (b[6]! & 0x0f) | 0x50;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/** Стабильный UUID карточки доски по `sourceKey`. */
export function crmBoardItemId(sourceKey: string): string {
  const ns = parseUuidBytes(CRM_BOARD_ITEM_NAMESPACE);
  const name = new TextEncoder().encode(sourceKey);
  const data = new Uint8Array(ns.length + name.length);
  data.set(ns, 0);
  data.set(name, ns.length);
  return formatUuidV5(sha1(data));
}

export function crmEventSourceKey(eventId: string): string {
  return `event:${eventId}`;
}

export function crmUserSourceKey(userId: string): string {
  return `user:${userId}`;
}

export function crmLeadSourceKey(userId: string): string {
  return `lead:${userId}`;
}

export function crmMembershipLeadSourceKey(leadId: string): string {
  return `membership_lead:${leadId}`;
}

export function crmManyChatSourceKey(contactId: string): string {
  return `manychat:${contactId}`;
}

export function crmManualLeadSourceKey(manualLeadId: string): string {
  return `manual:${manualLeadId}`;
}

export function formatManualLeadContactLabel(input: {
  displayName: string;
  telegramUsername: string | null;
  phone: string | null;
}): string {
  const name = input.displayName.trim().slice(0, 120);
  if (name) return name;
  if (input.telegramUsername) return `@${input.telegramUsername.replace(/^@/, '')}`;
  if (input.phone) return input.phone;
  return 'Лид';
}

export function normalizeInstagramUsername(raw: string | null | undefined): string | null {
  const v = raw?.replace(/^@/, '').trim().toLowerCase();
  return v || null;
}

export function formatManyChatContactLabel(input: {
  displayName: string | null;
  instagramUsername: string | null;
  manychatContactId: string;
}): string {
  if (input.displayName?.trim()) return input.displayName.trim().slice(0, 120);
  if (input.instagramUsername) return `@${input.instagramUsername}`;
  return `ManyChat ${input.manychatContactId.slice(0, 8)}`;
}

export function instagramProfileUrl(username: string | null | undefined): string | null {
  const u = normalizeInstagramUsername(username);
  return u ? `https://www.instagram.com/${u}/` : null;
}
